import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  adminApi,
  type AdminSubscription,
} from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import type { ColumnDef, RowAction } from "@/components/ui";
import {
  Download,
  ExternalLink,
  Eye,
} from "lucide-react";
import { AdminSubscriptionDetailModal } from "./AdminSubscriptionDetailModal";
import { AdminCompanyDetailModal } from "./AdminCompanyDetailModal";
import { downloadCsv } from "@/lib/utils";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

// ═══════════════════════════════════════════════════════════════════
// Status badge helpers
// ═══════════════════════════════════════════════════════════════════

const subscriptionStatusLabels: Record<string, string> = {
  active: "Aktiv",
  past_due: "Förfallen",
  canceled: "Avbruten",
  incomplete: "Ofullständig",
  trialing: "Provperiod",
  unpaid: "Obetald",
};

const subscriptionStatusStyles: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  past_due: "bg-red-100 text-red-700",
  canceled: "bg-gray-100 text-gray-700",
  incomplete: "bg-yellow-100 text-yellow-700",
  trialing: "bg-blue-100 text-blue-700",
  unpaid: "bg-red-100 text-red-700",
};

// ═══════════════════════════════════════════════════════════════════
// Filter options
// ═══════════════════════════════════════════════════════════════════

const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: "", label: "Alla statusar" },
  { value: "active", label: "Aktiv" },
  { value: "past_due", label: "Förfallen" },
  { value: "canceled", label: "Avbruten" },
  { value: "incomplete", label: "Ofullständig" },
];

// ═══════════════════════════════════════════════════════════════════
// Subscriptions Table
// ═══════════════════════════════════════════════════════════════════

function SubscriptionsTable() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    refresh,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  } = useDataTable<AdminSubscription, { status: string; userEmail: string; periodStart: string; createdAt: string }>({
    fetcher: (params) =>
      adminApi.listSubscriptions({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        status: params.status || undefined,
        userEmail: params.userEmail || undefined,
        periodStart: params.periodStart || undefined,
        createdAfter: params.createdAt || undefined,
        createdBefore: params.createdAt || undefined,
      }),
    pageSize: 25,
    initialFilters: { status: "", userEmail: "", periodStart: "", createdAt: "" },
    sortKeyMap: {
      company: "companyName",
      user: "userEmail",
      status: "status",
      period: "currentPeriodStart",
      createdAt: "createdAt",
    },
  });

  const handleDownloadReceipt = useCallback(async (row: AdminSubscription) => {
    const result = await adminApi.downloadSubscriptionReceipt(row.id);
    if (result.success && result.blob) {
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename || `kvitto-${row.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Kvitto nedladdat");
    } else {
      toast.error(result.error || "Kunde inte ladda ner kvitto");
    }
  }, []);

  const columns: ColumnDef<AdminSubscription>[] = useMemo(
    () => [
      {
        id: "company",
        header: "Företag",
        sortable: true,
        sortAccessor: (row) => row.companyName,
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök företag eller org.nr...",
        },
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-gray-900">
              {row.companyName}
            </p>
            <p className="text-xs text-gray-500">{row.organizationNumber}</p>
          </div>
        ),
      },
      {
        id: "user",
        header: "Användare",
        hideBelow: "md" as const,
        sortable: true,
        sortAccessor: (row) => row.userEmail || "",
        filter: {
          type: "text" as const,
          value: filters.userEmail,
          onChange: (v: string) => setFilter("userEmail", v),
          placeholder: "Sök e-post...",
        },
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {row.userEmail || "-"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        sortAccessor: (row) => row.status,
        filter: {
          type: "select" as const,
          value: filters.status,
          onChange: (v: string) => setFilter("status", v),
          options: SUBSCRIPTION_STATUS_OPTIONS,
        },
        cell: (row) => {
          const isCanceling =
            row.status === "active" && row.cancelAtPeriodEnd;
          return (
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                isCanceling
                  ? "bg-yellow-100 text-yellow-700"
                  : subscriptionStatusStyles[row.status] ||
                    "bg-gray-100 text-gray-700"
              }`}
            >
              {isCanceling
                ? "Avslutas"
                : subscriptionStatusLabels[row.status] || row.status}
            </span>
          );
        },
      },
      {
        id: "period",
        header: "Period",
        hideBelow: "lg" as const,
        sortable: true,
        sortAccessor: (row) =>
          row.currentPeriodStart
            ? new Date(row.currentPeriodStart)
            : new Date(0),
        filter: {
          type: "date" as const,
          value: filters.periodStart,
          onChange: (v: string) => setFilter("periodStart", v),
        },
        cell: (row) => {
          if (!row.currentPeriodStart || !row.currentPeriodEnd) {
            return <span className="text-sm text-gray-400">-</span>;
          }
          return (
            <span className="text-sm text-gray-600">
              {new Date(row.currentPeriodStart).toLocaleDateString("sv-SE")} –{" "}
              {new Date(row.currentPeriodEnd).toLocaleDateString("sv-SE")}
            </span>
          );
        },
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "lg" as const,
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        filter: {
          type: "date" as const,
          value: filters.createdAt,
          onChange: (v: string) => setFilter("createdAt", v),
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
    ],
    [search, setSearch, filters.userEmail, filters.status, filters.periodStart, filters.createdAt, setFilter]
  );

  const rowActions: RowAction<AdminSubscription>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Visa detaljer",
        icon: Eye,
        onClick: (row) => setSelectedId(row.id),
      },
      {
        key: "receipt",
        label: "Ladda ner kvitto",
        icon: Download,
        onClick: handleDownloadReceipt,
      },
      {
        key: "open-company",
        label: "Öppna företag",
        icon: ExternalLink,
        onClick: (row) => setSelectedCompanyId(row.companyId),
      },
    ],
    [handleDownloadReceipt]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="Inga prenumerationer hittades"
        pagination={pagination}
        onPageChange={setPage}
        pageSizeOptions={[25, 50, 100, 250]}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => setSelectedId(row.id)}
        rowActions={rowActions}
        serverSortColumnId={sortColumnId}
        serverSortDirection={sortDirection}
        onSort={handleSort}
        onResetFilters={resetFilters}
        toolbarExtra={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              downloadCsv(
                data,
                [
                  { header: "ID", accessor: (r) => r.id },
                  { header: "Företag", accessor: (r) => r.companyName },
                  { header: "Org.nummer", accessor: (r) => r.organizationNumber },
                  { header: "Användare", accessor: (r) => r.userEmail || "" },
                  { header: "Status", accessor: (r) => r.cancelAtPeriodEnd ? "Avslutas" : (subscriptionStatusLabels[r.status] || r.status) },
                  { header: "Period start", accessor: (r) => r.currentPeriodStart ? new Date(r.currentPeriodStart).toLocaleDateString("sv-SE") : "" },
                  { header: "Period slut", accessor: (r) => r.currentPeriodEnd ? new Date(r.currentPeriodEnd).toLocaleDateString("sv-SE") : "" },
                  { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
                ],
                `prenumerationer-${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
            disabled={data.length === 0}
          >
            <Download size={14} />
            Exportera CSV
          </Button>
        }
      />

      {selectedId && (
        <AdminSubscriptionDetailModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onRefresh={refresh}
        />
      )}

      {selectedCompanyId && (
        <AdminCompanyDetailModal
          companyId={selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
          onUpdated={refresh}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

export function AdminSubscriptions() {
  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.subscriptions.title} noIndex />
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Prenumerationer</h1>
          <p className="text-gray-600 mt-2">
            Hantera alla prenumerationer
          </p>
        </div>

        <SubscriptionsTable />
      </div>
    </>
  );
}
